/**
 * @format
 */

import { useState, useEffect } from 'react';
import {
  getRoles,
  getUserRoleMappings,
  updateUserProfile,
  resetUserPassword,
  assignRoleToUser,
  removeRoleFromUser,
} from '../../../core/services/keycloakApi';

import {
  REALM,
  CLIENT_NAME,
} from '../../../core/constants/keycloakCredentials';
import { getAllJobTitles } from '../../../core/services/jobTitle.service';
import { getAllGlobalRoles } from '../../../core/services/globalRole.service';
import {
  getUserInfo,
  updateUserInfo,
} from '../../../core/services/user.service';
import { getAllSkills } from '../../../core/services/skills.service';
import { getAllExpertise } from '../../../core/services/expertise.service';

export const useUserEditForm = (userId, formData, setFormData, onSuccess) => {
  const [tabIndex, setTabIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const [roles, setRoles] = useState([]);
  const [userRoles, setUserRoles] = useState([]);
  const [roleLoading, setRoleLoading] = useState(false);
  const [jobTitles, setJobTitles] = useState({
    selected: [],
    options: [],
  });
  const [globalRoles, setGlobalRoles] = useState({
    selected: [],
    options: [],
  });
  const [skills, setSkills] = useState({
    selected: [],
    options: [],
  });
  const [expertise, setExpertise] = useState({
    selected: [],
    options: [],
  });

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await updateUserProfile(userId, {
        firstName: formData.firstName,
        lastName: formData.lastName,
        email: formData.email,
      });

      onSuccess?.('User updated');
    } catch {
      onSuccess?.('Update failed', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleResetPassword = async () => {
    try {
      await resetUserPassword({
        realm: REALM,
        userId,
        newPassword,
        clientId: CLIENT_NAME,
      });

      onSuccess?.('Password reset');
      setResetOpen(false);
      setNewPassword('');
    } catch {
      onSuccess?.('Reset failed', 'error');
    }
  };

  const handleUpdateRole = async (event, newSelectedRole) => {
    if (!newSelectedRole) return;

    setRoleLoading(true);
    const currentRole = userRoles[0] || null;

    try {
      if (currentRole && currentRole.id !== newSelectedRole.id) {
        await removeRoleFromUser(userId, { ...currentRole });
      }

      await assignRoleToUser(userId, { ...newSelectedRole });

      setUserRoles([newSelectedRole]);
      onSuccess?.(`Role updated to "${newSelectedRole.name}"`);
    } catch {
      onSuccess?.('Failed to update role', 'error');
    } finally {
      setRoleLoading(false);
    }
  };

  // fields like job titles, skills and expertise
  const fetchUserProfile = async function () {
    const userInfo = await getUserInfo(formData.username);
    if (!userInfo) return;
    if (userInfo?.jobTitles?.length) {
      setJobTitles((prev) => ({ ...prev, selected: userInfo.jobTitles }));
    }
    if (userInfo?.globalRole?.length) {
      setGlobalRoles((prev) => ({
        ...prev,
        selected: userInfo.globalRole.map(Number),
      }));
    } else if (userInfo?.globalRoles?.length) {
      setGlobalRoles((prev) => ({
        ...prev,
        selected: userInfo.globalRoles.map(Number),
      }));
    }
    if (userInfo?.skillId?.length) {
      setSkills((prev) => ({ ...prev, selected: userInfo.skillId }));
    }
    if (userInfo?.expertiseId?.length) {
      setExpertise((prev) => ({ ...prev, selected: userInfo.expertiseId }));
    }
  };

  // This updates fields like Job titles, Skills or Expertise
  const handleProfileUpdate = async (fieldName, values, successMsg) => {
    if (!values || !values.length) return;

    const payload = {
      userId,
      userName: formData.username,
      [fieldName]: values,
    };

    try {
      await updateUserInfo(payload);
      onSuccess?.(successMsg);
    } catch {
      onSuccess?.('Failed to update profile', 'error');
    }
  };

  useEffect(() => {
    if (!userId) return;
    fetchUserProfile();
  }, [userId]);

  useEffect(() => {
    if (!userId) return;

    Promise.all([
      getRoles(),
      getUserRoleMappings(userId),
      getAllJobTitles(),
      getAllGlobalRoles(),
      getAllSkills(),
      getAllExpertise(),
    ]).then(
      ([
        rolesRes,
        userRolesRes,
        jobTitlesRes,
        globalRolesRes,
        skillsRes,
        expertiseRes,
      ]) => {
      setRoles(rolesRes?.data?.data || []);
      const mappings = userRolesRes?.data?.data?.clientMappings;
      setUserRoles(
        mappings
            ? Object.values(mappings).flatMap((c) => c.mappings || [])
            : []
      );
      setJobTitles((prev) => ({ ...prev, options: jobTitlesRes || [] }));
        setGlobalRoles((prev) => ({ ...prev, options: globalRolesRes || [] }));
      setSkills((prev) => ({ ...prev, options: skillsRes || [] }));
        setExpertise((prev) => ({ ...prev, options: expertiseRes || [] }));
    }
    );
  }, [userId]);

  return {
    tabIndex,
    skills,
    setSkills,
    expertise,
    setExpertise,
    jobTitles,
    setJobTitles,
    globalRoles,
    setGlobalRoles,
    setTabIndex,
    loading,
    resetOpen,
    setResetOpen,
    newPassword,
    setNewPassword,
    showPassword,
    setShowPassword,
    roles,
    userRoles,
    roleLoading,
    handleUpdateUser,
    handleResetPassword,
    handleUpdateRole,
    handleProfileUpdate,
  };
};
